/**
 * Triggers a browser download/open for an invoice PDF blob from GET /invoice/pdf.
 */
export function downloadInvoicePdfBlob(blob, filename = 'invoice.pdf') {
  if (typeof window === 'undefined' || !blob) return;

  const pdfBlob = blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' });
  const fileURL = window.URL.createObjectURL(pdfBlob);
  const fileLink = document.createElement('a');
  fileLink.href = fileURL;
  fileLink.setAttribute('download', filename);
  fileLink.setAttribute('target', '_blank');
  document.body.appendChild(fileLink);
  fileLink.click();
  fileLink.remove();
  window.URL.revokeObjectURL(fileURL);
}

function decodeBytes(bytes) {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(bytes).toString('utf8');
  }
  return String.fromCharCode(...bytes);
}

/** Reject empty payloads and JSON/HTML error bodies returned with a blob responseType. */
async function readBlobBytes(blob, start = 0, end = blob.size) {
  if (typeof blob.arrayBuffer === 'function') {
    const buffer = await blob.arrayBuffer();
    return new Uint8Array(buffer).slice(start, end);
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(new Uint8Array(reader.result).slice(start, end));
    };
    reader.onerror = () => reject(reader.error || new Error('Unable to read PDF response.'));
    reader.readAsArrayBuffer(blob);
  });
}

async function readBlobPrefix(blob, length = 4) {
  const bytes = await readBlobBytes(blob, 0, length);
  return decodeBytes(bytes);
}

async function readBlobText(blob) {
  if (typeof blob.text === 'function') {
    return blob.text();
  }
  const bytes = await readBlobBytes(blob);
  return decodeBytes(bytes);
}

export async function assertInvoicePdfBlob(blob, fallback = 'Unable to download invoice PDF.') {
  if (!blob) {
    throw new Error(fallback);
  }

  const pdfBlob = blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' });
  if (pdfBlob.size < 5) {
    throw new Error('Invoice PDF is empty or invalid.');
  }

  const header = await readBlobPrefix(pdfBlob, 4);
  if (header.startsWith('%PDF')) {
    return pdfBlob;
  }

  try {
    const text = await readBlobText(pdfBlob);
    const parsed = JSON.parse(text);
    throw new Error(parsed?.message || fallback);
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error(fallback);
    }
    throw error;
  }
}

export function buildInvoicePdfFilename(invoice) {
  const docNumber = invoice?.doc_number;
  if (docNumber) {
    const safe = String(docNumber).replace(/[^\w.-]+/g, '_');
    return `invoice-${safe}.pdf`;
  }
  if (invoice?.invoice_id) {
    return `invoice-${invoice.invoice_id}.pdf`;
  }
  return 'invoice.pdf';
}
